import type { TradingSessionStatus } from '@/lib/api/tradingSession';

export type TradingSessionUrgency = 'none' | 'warning' | 'critical' | 'expired';

export function getTradingSessionUrgency(
    remainingMs: number,
): TradingSessionUrgency {
    if (remainingMs <= 0) return 'expired';
    if (remainingMs <= 10_000) return 'critical';
    if (remainingMs <= 60_000) return 'warning';
    return 'none';
}

export function getServerAdjustedNow(
    status: Pick<TradingSessionStatus, 'serverTimeMs'>,
    localNow = Date.now(),
    observedAtMs = localNow,
): number {
    return localNow + (status.serverTimeMs - observedAtMs);
}

export function isTradingAuthorityActive(
    status: TradingSessionStatus | null,
    serverAdjustedNow = Date.now(),
): boolean {
    if (!status) return false;
    if (status.state !== 'active') return false;
    return (
        typeof status.expiresAtMs === 'number' &&
        status.expiresAtMs > serverAdjustedNow
    );
}

function normalizePath(pathOrUrl: string): string {
    try {
        return new URL(pathOrUrl, 'https://api.wolfswap.app').pathname;
    } catch {
        return pathOrUrl.split('?')[0] ?? pathOrUrl;
    }
}

export function isSignerMutationRequest(
    method: string,
    pathOrUrl: string,
): boolean {
    if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
        return false;
    }

    const path = normalizePath(pathOrUrl);
    if (
        [
            '/v2/custodial/swap',
            '/v2/custodial/wolfage/topup',
            '/v2/custodial/association-signature',
        ].includes(path)
    )
        return true;

    if (!path.startsWith('/speed/')) return false;
    if (
        path === '/speed/quote' ||
        /^\/speed\/job\/[^/]+\/tx-hashes$/.test(path)
    ) {
        return false;
    }

    return (
        path === '/speed/deposit' ||
        path === '/speed/withdraw' ||
        path.startsWith('/speed/trade/') ||
        path.startsWith('/speed/agent/') ||
        /^\/speed\/job\/[^/]+\/reauthorize$/.test(path)
    );
}
